/**
 * HashFocus - A module that manages focus when URL hash changes
 * Provides accessible focus handling for hash-based navigation
 */
export interface HashFocusOptions {
  /** Enable smooth scrolling when focusing elements (default: true) */
  smoothScroll?: boolean;
  /** CSS class added to elements when focused via hash (default: 'hash-focus') */
  focusClass?: string;
  /** Delay in ms before setting focus after page load (default: 100) */
  initialDelay?: number;
}

export class HashFocus {
  private options: Required<HashFocusOptions>;
  private initialized: boolean = false;
  private boundHandleHashChange: (event: HashChangeEvent) => void;

  /**
   * Create a new HashFocus instance
   * @param options - Configuration options
   */
  constructor(options: HashFocusOptions = {}) {
    this.options = {
      smoothScroll: options.smoothScroll ?? true,
      focusClass: options.focusClass ?? 'hash-focus',
      initialDelay: options.initialDelay ?? 100
    };
    
    // Bind the event handler once to maintain the same reference
    this.boundHandleHashChange = this.handleHashChange.bind(this);
  }

  /**
   * Initialize the hash focus handling
   * @returns The HashFocus instance (for chaining)
   */
  public init(): HashFocus {
    if (this.initialized) {
      return this;
    }

    // Check for required browser features
    if (!this.isBrowserSupported()) {
      console.warn('HashFocus: Browser does not support required features');
      return this;
    }

    // Initial check for hash on page load
    if (window.location.hash) {
      const initialId = window.location.hash.substring(1);
      // Use setTimeout to ensure DOM is fully loaded
      setTimeout(() => this.setFocusToHashTarget(initialId), this.options.initialDelay);
    }

    // Listen for hash changes
    window.addEventListener('hashchange', this.boundHandleHashChange, false);

    this.initialized = true;
    return this;
  }

  /**
   * Check if the browser supports required features
   */
  private isBrowserSupported(): boolean {
    return (
      typeof document !== 'undefined' &&
      typeof window !== 'undefined' &&
      typeof document.getElementById === 'function' &&
      typeof window.addEventListener === 'function'
    );
  }

  /**
   * Handle hash change events
   */
  private handleHashChange(): void {
    const hashId = this.getHashId();
    this.setFocusToHashTarget(hashId);
  }
  
  /**
   * Get the hash ID from the URL
   * Protected to allow overriding in tests
   */
  protected getHashId(): string {
    return window.location.hash.substring(1);
  }

  /**
   * Sets focus to the element matching the hash ID
   * @param hashId - The ID from the URL hash
   */
  // Change to public so tests can access it directly
  public setFocusToHashTarget(hashId: string): void {
    // Valid ID must only contain alphanumeric characters, underscores, or hyphens
    const isValidId = /^[A-z0-9_-]+$/.test(hashId);

    if (!isValidId) {
      return;
    }

    const element = document.getElementById(hashId);

    if (!element) {
      return;
    }

    // Check if element is not already naturally focusable
    const naturallyFocusable = /^(?:a|select|input|button|textarea|iframe|audio|video)$/i;

    if (!naturallyFocusable.test(element.tagName)) {
      // Set tabIndex to make it focusable
      element.setAttribute('tabindex', '-1');

      // Add a class that can be used for styling the focused element
      if (this.options.focusClass) {
        element.classList.add(this.options.focusClass);

        // Remove the class when focus leaves
        const onBlur = (): void => {
          element.classList.remove(this.options.focusClass);
          element.removeEventListener('blur', onBlur);
        };

        element.addEventListener('blur', onBlur);
      }
    }

    // Scroll element into view and focus it
    if (this.options.smoothScroll) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      element.focus({ preventScroll: true });
    } else {
      element.focus();
    }
  }

  /**
   * Clean up event listeners and references
   */
  public destroy(): void {
    if (!this.initialized) {
      return;
    }

    window.removeEventListener('hashchange', this.boundHandleHashChange);
    this.initialized = false;
  }
}

/**
 * Create and initialize a HashFocus instance with default options
 * @returns An initialized HashFocus instance
 */
export function createHashFocus(options?: HashFocusOptions): HashFocus {
  return new HashFocus(options).init();
}

// Default export for convenience
export default HashFocus;
